import { useParams } from 'react-router-dom';
import { PermissionGuard } from '@/features/auth';
import { useAddOnQuery } from '../hooks/useAddOnQuery';
import { useAddOnMutations } from '../hooks/useAddOnMutations';
import { AssignedProductsPanel } from '../components/AssignedProductsPanel';
import { Alert, Button, ButtonLink, TextLink } from '@/shared/components';

export function AddOnDetail() {
    const { id } = useParams<{ id: string }>();
    const query = useAddOnQuery(id);
    const { retireMutation } = useAddOnMutations();

    if (!id) return <p className="text-mp-error">Invalid optional extra ID.</p>;
    if (query.isLoading) return <p>Loading optional extra...</p>;
    if (query.isError || !query.data) {
        return <Alert variant="error"><span>Optional extra not found or failed to load.</span></Alert>;
    }

    const addon = query.data;

    return (
        <section className="space-y-4">
            <div className="flex items-center gap-2">
                <TextLink to="/smr-admin/addons" color="secondary" className="text-sm">&larr; Back to Optional Extras</TextLink>
            </div>

            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">{addon.name}</h1>
                <div className="flex gap-2">
                    <PermissionGuard permission={{ resource: 'smr', action: 'addon.edit' }}>
                        <ButtonLink to={`/smr-admin/addons/${id}/edit`} variant="outline" size="xs">Edit</ButtonLink>
                    </PermissionGuard>
                    {!addon.retired_at && (
                        <PermissionGuard permission={{ resource: 'smr', action: 'addon.delete' }}>
                            <Button
                                type="button"
                                variant="warning"
                                appearance="solid"
                                size="xs"
                                onClick={() => retireMutation.mutate(id)}
                                disabled={retireMutation.isPending}
                            >
                                Retire
                            </Button>
                        </PermissionGuard>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                    <p className="text-sm text-mp-muted">Category</p>
                    <p className="font-medium">{addon.category ?? '—'}</p>
                </div>
                <div>
                    <p className="text-sm text-mp-muted">Status</p>
                    <p className="font-medium">{addon.retired_at ? 'Retired' : 'Active'}</p>
                </div>
                <div>
                    <p className="text-sm text-mp-muted">Net Price</p>
                    <p className="font-medium font-mono">£{addon.price}</p>
                </div>
                <div>
                    <p className="text-sm text-mp-muted">Gross Price</p>
                    <p className="font-medium font-mono">£{addon.gross_price}</p>
                </div>
                {addon.vat_category && (
                    <div>
                        <p className="text-sm text-mp-muted">VAT Category</p>
                        <p className="font-medium">
                            {addon.vat_category.name} ({Number(addon.vat_category.rate_percent).toFixed(1)}%)
                        </p>
                    </div>
                )}
                {addon.vat_amount && (
                    <div>
                        <p className="text-sm text-mp-muted">VAT Amount</p>
                        <p className="font-medium font-mono">£{addon.vat_amount}</p>
                    </div>
                )}
                {addon.description && (
                    <div className="md:col-span-2">
                        <p className="text-sm text-mp-muted">Description</p>
                        <p className="font-medium">{addon.description}</p>
                    </div>
                )}
            </div>

            <div className="mt-6">
                <AssignedProductsPanel addOnId={id} />
            </div>
        </section>
    );
}
